import { createApi } from '@redux/createApi';
import { createSlice } from '@reduxjs/toolkit';
import { serialize } from '@shared/lib/serialize';
import { format } from '../../../shared/lib/format';

const tags = {
  getUsers: 'getUsers',
  getUsersDetails: 'getUsersDetails',
  getUserTransactions: 'getUserTransactions',
  getUserConversions: 'getUserConversions',
  getUserBinanceDeposits: 'getUserBinanceDeposits',
  getUserMonthlyDepositsKgs: 'getUserMonthlyDepositsKgs',
  getUserAssets: 'getUserAssets',
  getUserBalances: 'getUserBalances',
  getUserBinanceBalances: 'getUserBinanceBalances',
  getRealBinanceBalance: 'getRealBinanceBalance',
  getWalletHistory: 'getWalletHistory',
  getUserHistory: 'getUserHistory',
  activateUser: 'activateUser',
  deactivateUser: 'deactivateUser',
  setRole: 'setRole',
  deleteCard: 'deleteCard',
  resetKyc: 'resetKyc',
  manualSetRub: 'manualSetRub',
  getKsbEmailUsers: 'getKsbEmailUsers',
};

export const usersApi = createApi({
  reducerPath: 'usersApi',
  tagTypes: [
    tags.getUsers,
    tags.getUsersDetails,
    tags.getUserTransactions,
    tags.getUserConversions,
    tags.getUserBinanceDeposits,
    tags.getUserMonthlyDepositsKgs,
    tags.getUserAssets,
    tags.getUserBalances,
    tags.getUserBinanceBalances,
    tags.getRealBinanceBalance,
    tags.getWalletHistory,
    tags.getUserHistory,
    tags.activateUser,
    tags.deactivateUser,
    tags.resetKyc,
    tags.manualSetRub,
    tags.getKsbEmailUsers
  ],
  endpoints: (builder) => ({
    getUsersDetails: builder.query({
      query: ({ id }) => {
        return ({
          method: 'get', url: `/admin/users/${id}`,
        });
      },
      providesTags: [tags.getUsersDetails],
    }),
    getUsers: builder.query({
      query: (queryParams) => {
        const [page, limit, filter, search, binance_status, start, end, role, user_partner] = queryParams;
        const formatDate = (date) => {
          if (!date) return null;
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        };

        return ({
          method: 'get', url: `/admin/users?${serialize({
            page_number: page,
            page_size: limit,
            _filter: filter,
            order: 'DESC',
            ...(binance_status ? { binance_status } : {}),
            ...(start ? { start: formatDate(start) } : {}),
            ...(end ? { end: formatDate(end) } : {}),
            ...(role ? { role } : {}),
            ...(user_partner ? { user_partner } : {}),
            ...search === '' || !search ? {} : { search },
          })}`,
        });
      },
      providesTags: [tags.getUsers],
      serializeQueryArgs: ({ queryArgs }) => {
        const newQueryArgs = { ...queryArgs };
        if (newQueryArgs.page) {
          delete newQueryArgs.page;
        }
        return newQueryArgs;
      },
      transformResponse: (data) => {
        return {
          list: data.users,
          count: data.page_amount,
          totalCount: data.total_users || data.user_count || null,
        };
      },
    }),
    getKsbEmailUsers: builder.query({
      query: () => ({
        method: 'get', url: `/admin/ksb/email/users`,
      }),
      providesTags: [tags.getKsbEmailUsers],
      // Возвращает голый массив, а не привычный конверт { list, page_amount }.
      // Пагинации на бэке нет — она делается на клиенте.
      transformResponse: (data) => (Array.isArray(data) ? data : []),
    }),
    getUserTransactions: builder.mutation({
      query: ({ userId, page, limit, start, end, tx_type, asset, tx_status, from_amount, to_amount }) => {
        return ({
          method: 'post', url: `/transaction-info/transaction/users/${userId}?${serialize({
            page_number: page,
            page_size: limit,
            ...start === '' || !start ? {} : { start: `${start.getDate()}/${format.addZeroBefore(start.getMonth() + 1)}/${start.getFullYear()}` },
            ...end === '' || !end ? {} : { end: `${end.getDate()}/${format.addZeroBefore(end.getMonth() + 1)}/${end.getFullYear()}` },
            ...tx_type ? { tx_type } : {},
            ...asset ? { asset } : {},
            ...tx_status ? { status: tx_status } : {},
            ...from_amount ? { amount_from: format.parseAmount(from_amount) } : {},
            ...to_amount ? { amount_to: format.parseAmount(to_amount) } : {},
          })}`,
          body: {},

        });
      },
      providesTags: [tags.getUserTransactions],
      transformResponse: (data) => {
        return {
          list: data.transaction_list,
          count: data.page_amount,
        };
      },
    }),
    getUserBinanceDeposits: builder.mutation({
      query: ({ userId, start_date, end_date }) => ({
        method: 'get',
        url: `/admin/deposit-history?${serialize({
          user_id: userId,
          ...(start_date ? { start_date: `${start_date.getDate()}/${format.addZeroBefore(start_date.getMonth() + 1)}/${start_date.getFullYear()}` } : {}),
          ...(end_date ? { end_date: `${end_date.getDate()}/${format.addZeroBefore(end_date.getMonth() + 1)}/${end_date.getFullYear()}` } : {}),
        })}`,
      }),
      providesTags: [tags.getUserBinanceDeposits],
      transformResponse: (data) => ({
        list: data || [],
        count: (data || []).length,
      }),
    }),
    getUserMonthlyDepositsKgs: builder.query({
      query: ({ userId, page = 1, pageSize = 50 }) => ({
        method: 'get',
        url: `/admin/user-monthly-deposit?${serialize({
          user_id: userId,
          page_number: page,
          page_size: pageSize,
        })}`,
      }),
      providesTags: [tags.getUserMonthlyDepositsKgs],
      transformResponse: (data) => ({
        list: (data?.monthly_deposit_list || [])
          .map(item => ({
            ...item,
            total_amount: item.total,
          }))
          .sort((a, b) => {
            const dateA = new Date(a.year, a.month - 1);
            const dateB = new Date(b.year, b.month - 1);
            return dateB - dateA;
          }),
        pageAmount: data?.page_amount || 1,
      }),
    }),
    getUserConversations: builder.mutation({
      query: ({ userId, page, limit, start, end }) => {
        return ({
          method: 'post', url: `/transaction-info/conversion/users/${userId}?${serialize({
            page_number: page,
            page_size: limit,
            ...start === '' || !start ? {} : { start: `${start.getDate()}/${format.addZeroBefore(start.getMonth() + 1)}/${start.getFullYear()}` },
            ...end === '' || !end ? {} : { end: `${end.getDate()}/${format.addZeroBefore(end.getMonth() + 1)}/${end.getFullYear()}` },

          })}`,
          body: {

          },
        });
      },
      providesTags: [tags.getUserConversions],
      transformResponse: (data) => {
        return {
          list: data.conversion_list,
          count: data.page_amount,
        };
      },
    }),
    getUserAssets: builder.query({
      query: ({ userId }) => ({
        method: 'get',
        url: `/admin/used-assets?user_id=${userId}`,
      }),
      providesTags: [tags.getUserAssets],
    }),
    // Актуальные балансы юзера по всем активам.
    // Без {user_id} эндпоинт отдаёт агрегат по всем пользователям и здесь не годится.
    getUserBalances: builder.query({
      query: ({ userId }) => ({
        method: 'get',
        url: `/admin/users/balances/${userId}`,
      }),
      providesTags: [tags.getUserBalances],
    }),
    getUserBinanceBalance: builder.query({
      query: ({ userId, asset }) => ({
        method: 'get',
        url: `/admin/users/binance-balances?${serialize({
          user_id: userId,
          asset: asset
        })}`,
      }),
      providesTags: [tags.getUserBinanceBalances],
    }),
    getRealBinanceBalance: builder.query({
      query: ({ userId, asset }) => ({
        method: 'get',
        url: `/admin/real-binance-balance?${serialize({
          user_pk: `USER#${userId}`,
          asset: asset
        })}`,
      }),
      providesTags: [tags.getRealBinanceBalance],
    }),
    getWalletHistory: builder.query({
      query: ({ userId, asset, start, end, page = 1, pageSize = 10 }) => ({
        method: 'get',
        url: `/admin/wallet-history?${serialize({
          user_id: userId,
          asset: asset,
          page_number: page,
          page_size: pageSize,
          ...(start ? { start } : {}),
          ...(end ? { end } : {}),
        })}`,
      }),
      providesTags: [tags.getWalletHistory],
      transformResponse: (data) => ({
        list: data.list || [],
        count: data.page_amount || 1
      }),
    }),
    getUserHistory: builder.query({
      query: ({ userPk, pageNumber = 1, pageSize = 50, start, end }) => ({
        method: 'get',
        url: `/user-history/user-pk-history?${serialize({
          user_pk: userPk,
          page_number: pageNumber,
          page_size: pageSize,
          ...(start ? { start } : {}),
          ...(end ? { end } : {}),
        })}`,
      }),
      providesTags: [tags.getUserHistory],
      transformResponse: (data) => ({
        list: data.history_list || [],
        pageNumber: data.page_number || 1,
        pageAmount: data.page_amount || 1
      }),
    }),
    setUserRole: builder.mutation({
      query: ({ pk, role }) => {
        return ({
          method: 'post', url: `/role/set-user-role`,
          body: {
            pk,
            role,
          },
        });
      },
      providesTags: [tags.setRole],
      invalidatesTags: [tags.getUsers],
    }),
    updateUserRole: builder.mutation({
      query: ({ userId, role }) => ({
        url: `/admin/user/${userId}/role`,
        method: 'PUT',
        body: { role },
      }),
      invalidatesTags: [tags.getUsers],
    }),
    deactivateUser: builder.mutation({
      query: ({ pk, reason }) => ({
        url: `/admin/user/deactivate`,
        method: 'post',
        body: {
          pk,
          reason
        }
      }),
      invalidatesTags: [tags.getUsers],
    }),
    activateUser: builder.mutation({
      query: (id) => ({
        url: `/admin/user/activate?${serialize({ pk: id })}`,
        method: 'get'
      }),
      invalidatesTags: [tags.getUsers],
    }),
    deleteCard: builder.mutation({
      query: ({ pk }) => {
        return ({
          method: 'delete', url: `/virtual-card/admin/delete?pk=${encodeURIComponent(pk)}`,
        });
      },
      providesTags: [tags.deleteCard],
      invalidatesTags: [tags.getUsersDetails],
    }),
    resetKyc: builder.mutation({
      query: ({ userId }) => ({
        url: `/admin/reset/kyc?${serialize({ user_id: userId })}`,
        method: 'post'
      }),
      invalidatesTags: [tags.getUsersDetails]
    }),
    manualSetRub: builder.mutation({
      query: ({ user_pk, amount }) => ({
        url: `/manual/set-rub`,
        method: 'post',
        body: {
          user_pk,
          amount
        }
      }),
      providesTags: [tags.manualSetRub],
      invalidatesTags: [tags.getUsersDetails]
    }),
  }),
});

export const usersParams = createSlice({
  name: 'usersParams',
  initialState: {
    page: 1,
    limit: 100,
    filter: 'ALL',
    search: '',
    binance_status: '',
    role: '',
    user_partner: ''
  },
  reducers: {
    setParams: (state, action) => {
      return { ...state, ...action.payload };
    },
  },
});

export const {
  useGetUsersQuery,
  useGetKsbEmailUsersQuery,
  useGetUsersDetailsQuery,
  useGetUserTransactionsMutation,
  useGetUserConversationsMutation,
  useGetUserBinanceDepositsMutation,
  useGetUserMonthlyDepositsKgsQuery,
  useGetUserAssetsQuery,
  useGetUserBalancesQuery,
  useGetUserBinanceBalanceQuery,
  useGetRealBinanceBalanceQuery,
  useGetWalletHistoryQuery,
  useGetUserHistoryQuery,
  useSetUserRoleMutation,
  useUpdateUserRoleMutation,
  useDeactivateUserMutation,
  useActivateUserMutation,
  useDeleteCardMutation,
  useResetKycMutation,
  useManualSetRubMutation,
} = usersApi;

export const { setParams } = usersParams.actions;
export const usersParamsSelector = (state) => state.usersParams;